import { useEffect, useState } from "react";
import { Button, Drawer, Input, Switch, Tabs, type DrawerProps } from "antd";
import Breadcrumb from "../../../Layout/Breadcrumb/Breadcrumb.js";
import useMobileGrid from "../../shared/grid/useMobileGrid.ts";
import { Moon, Sun, UserCircle } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { getDirection, t, useStoreLanguage } from "../../../features/store-language/i18n.ts";
import { usePortalShellPreferences } from "../../../Layout/PortalShell/PortalShellPreferences.tsx";
import LoadingRing from "../../shared/LoadingRing.jsx";
import { getShopLoginPath, isAgencyShopSession } from "../Orders/shopSession.ts";
import { getCustomerProfile } from "../Auth/customerIdentityService.ts";
import { agencyRoleLabel } from "../Auth/agencyRoleLabel.ts";
import AgencyPasswordForm from "./AgencyPasswordForm.tsx";
import AgencyContactForm from "./AgencyContactForm.tsx";
import "./CustomerProfile.css";

interface CustomerProfileData {
  userName: string;
  agencyTitle: string;
  role: string;
  fullName: string;
  phoneNumber: string;
  email?: string;
  userId: string;
}

interface CustomerProfileProps {
  open: boolean;
  onClose: () => void;
  page?: boolean;
}

function ProfileSurface({ page, children, ...props }: DrawerProps & { page: boolean }) {
  if (page) {
    return (
      <section className="customer-profile-page customer-profile-drawer">
        <Breadcrumb items={[{ title: t("حساب من") }]} />
        {children}
      </section>
    );
  }
  return (
    <Drawer {...props}>
      {children}
    </Drawer>
  );
}

export function CustomerProfilePage() {
  const mobile = useMobileGrid();
  const navigate = useNavigate();
  return (
    <CustomerProfile
      open
      page={mobile}
      onClose={function () { navigate("/account/orders", { replace: true }); }}
    />
  );
}

type ProfileFieldProps = {
  label: string;
  note?: string;
  technical?: boolean;
  value: string;
};

function ProfileField({ label, note, technical = false, value }: ProfileFieldProps) {
  return (
    <label className={`customer-profile-field${technical ? " is-technical" : ""}`}>
      <span className="customer-profile-field__label">{label}</span>
      <Input
        aria-label={label}
        readOnly
        value={value || "—"}
      />
      {note ? (
        <span className="customer-profile-field__note">{note}</span>
      ) : null}
    </label>
  );
}


export default function CustomerProfile({ open, onClose, page = false }: CustomerProfileProps) {
  const storeLanguage = useStoreLanguage();
  const navigate = useNavigate();
  const preferences = usePortalShellPreferences();
  const [profile, setProfile] = useState<CustomerProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("account");
  const [contactPending, setContactPending] = useState(false);

  useEffect(function () {
    if (!open) {
      return undefined;
    }
    let active = true;
    setLoading(true);

    async function loadProfile() {
      if (!isAgencyShopSession()) {
        navigate(getShopLoginPath(), { replace: true });
        return;
      }

      try {
        const data = await getCustomerProfile();
        if (active && data) {
          setProfile(data);
        }
      } catch {
        navigate(getShopLoginPath(), { replace: true });
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    void loadProfile();
    return function () {
      active = false;
    };
  }, [storeLanguage, navigate, open]);

  const fullName = profile?.fullName?.trim() || t("کاربر نمایندگی");
  const role = profile ? agencyRoleLabel(profile.role) : t("کاربر نمایندگی");
  const tabs = [
    {
      key: "account",
      label: t("حساب کاربری"),
      children: profile ? (
        <section className="customer-profile-section">
          <header className="customer-profile-section__header">
            <h2>{t("اطلاعات حساب")}</h2>
            <p>{t("اطلاعات تماس ثبت‌شده قابل جایگزینی نیست. شماره موبایل یا ایمیل خالی را می‌توانید پس از تأیید مالکیت اضافه کنید.")}</p>
          </header>
          <div className="customer-profile-section__body">
            <div className="customer-profile-fields">
              <ProfileField
                label={t("نام کامل")}
                value={profile.fullName}
              />
              <ProfileField
                label={t("نمایندگی فروش")}
                value={profile.agencyTitle}
              />
              <ProfileField
                label={t("نام کاربری")}
                technical
                value={profile.userName}
              />
              <ProfileField
                label={t("شماره موبایل")}
                technical
                value={profile.phoneNumber}
              />
              <ProfileField
                label={t("ایمیل")}
                technical
                value={profile.email || ""}
              />
              <ProfileField
                label={t("نقش در نمایندگی")}
                value={role}
              />
            </div>
            {open && (!profile.phoneNumber || !profile.email) && (
              <AgencyContactForm
                key={profile.userId}
                isEmail={!profile.email}
                onConfirmed={function (recipient, isEmail) {
                  setContactPending(false);
                  setProfile(function (current) {
                    if (!current) return current;
                    return isEmail
                      ? { ...current, email: recipient }
                      : { ...current, phoneNumber: recipient };
                  });
                }}
                onPendingChange={setContactPending}
              />
            )}
          </div>
        </section>
      ) : null,
    },
    {
      key: "personalization",
      label: t("شخصی‌سازی"),
      children: (
        <section className="customer-profile-section">
          <header className="customer-profile-section__header">
            <h2>{t("نمایش و خوانایی")}</h2>
            <p>{t("تنظیمات نمایش روی همین دستگاه ذخیره و بلافاصله روی پورتال اعمال می‌شود.")}</p>
          </header>
          <div className="customer-profile-section__body">
            <div className="customer-profile-setting">
              <span className="customer-profile-setting__icon" aria-hidden="true">
                {preferences?.dark ? <Moon size={24} /> : <Sun size={24} />}
              </span>
              <span className="customer-profile-setting__copy">
                <strong>{t("ظاهر پورتال")}</strong>
                <small>
                  {preferences?.dark ? t("حالت تیره") : t("حالت روشن")}
                </small>
              </span>
              <Switch
                aria-label={t("ظاهر پورتال")}
                checked={preferences?.dark ?? false}
                disabled={!preferences}
                onChange={function (checked) {
                  preferences?.setDark(checked);
                }}
              />
            </div>
          </div>
        </section>
      ),
    },
    {
      key: "security",
      label: t("امنیت"),
      children: (
        <section className="customer-profile-section">
          <header className="customer-profile-section__header">
            <h2>{t("تغییر رمز عبور")}</h2>
            <p>{t("برای امنیت بیشتر، رمزی انتخاب کنید که در سامانه دیگری استفاده نکرده‌اید.")}</p>
          </header>
          <div className="customer-profile-section__body">
            <AgencyPasswordForm />
          </div>
        </section>
      ),
    },
  ];

  return (
    <ProfileSurface
      page={page}
      className="customer-profile-drawer"
      rootClassName="customer-profile-drawer-root"
      footer={
        <div className="customer-profile-drawer__footer">
          <Button
            onClick={onClose}
            disabled={contactPending}
          >
            {t("بستن")}
          </Button>
        </div>
      }
      maskClosable={!contactPending}
      keyboard={!contactPending}
      onClose={function () { if (!contactPending) onClose(); }}
      open={open}
      placement={getDirection() === "rtl" ? "right" : "left"}
      title={
        <span className="customer-profile-drawer__title">
          <strong>{t("پورتال من")}</strong>
          <span>{profile?.fullName || ""}</span>
        </span>
      }
      width="min(46rem, 100vw)"
    >
      {loading ? (
        <div className="customer-profile-loading">
          <LoadingRing tip={t("در حال دریافت اطلاعات حساب کاربری")} />
        </div>
      ) : profile ? (
        <div className="customer-profile-drawer__content">
          <div className="customer-profile-summary">
            <span className="customer-profile-summary__media" aria-hidden="true">
              <UserCircle size={50} weight="light" />
            </span>
            <span className="customer-profile-summary__identity">
              <small>{role}</small>
              <strong>{fullName}</strong>
            </span>
            <span className="customer-profile-summary__stat">
              <small>{t("نام کاربری")}</small>
              <bdi dir="ltr">{profile.userName || "—"}</bdi>
            </span>
            <span className="customer-profile-summary__stat">
              <small>{t("شماره موبایل")}</small>
              <bdi dir="ltr">{profile.phoneNumber || "—"}</bdi>
            </span>
          </div>
          <Tabs
            activeKey={activeTab}
            aria-label={t("بخش‌های پروفایل")}
            items={tabs}
            onChange={function (key) { if (!contactPending) setActiveTab(key); }}
          />
        </div>
      ) : null}
    </ProfileSurface>
  );
}
